import React from 'react';

import { ColumnDef, Row } from '@tanstack/react-table';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { UserAccessStatus } from 'api/endPoints/GetReportAccessStatus';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { AvatarCell } from 'components/common/Tables/Cells/AvatarCell/AvatarCell';
import { DynamicChipsCell } from 'components/common/Tables/Cells/DynamicChipsCell/DynamicChipsCell';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';

import { AllowAccessCell } from 'components/Dashboards/Reporting/ReportSettingsModal/components/ReportSettingsForm/components/ReportAccessSettings/components/SpecificUsersAccess/components/UsersAccessTable/AllowAccessCell/AllowAccessCell';
import { NotifyOnUploadCell } from 'components/Dashboards/Reporting/ReportSettingsModal/components/ReportSettingsForm/components/ReportAccessSettings/components/SpecificUsersAccess/components/UsersAccessTable/NotifyOnUploadCell/NotifyOnUploadCell';
import { Chip } from 'components/DesignSystem/Chip';

// sort by access according to these rules( the rules below are for descending order, in ascending order it will be the opposite):
// users with canHaveAccess = false should be at the end. Users with hasAccess = true should come before users with hasAccess = false;
// We use descending order to put all users without the ability to have access at the bottom
const customSortByAccess = (rowA: Row<UserAccessStatus>, rowB: Row<UserAccessStatus>) => {
    const weight = ({ canHaveAccess, hasAccess }: UserAccessStatus) => {
        if (!canHaveAccess) return 0;
        return hasAccess ? 2 : 1;
    };

    return weight(rowA.original) - weight(rowB.original);
};

export const usersAccessTableColumns: ColumnDef<UserAccessStatus>[] = [
    {
        header: 'User',
        ...columnsCommonConfig.reportSettingsUserName,
        accessorFn: (row) => `${row.userDetails.firstName || ''} ${row.userDetails.lastName || ''}`.trim(),
        filterFn: 'includesString',
        sortingFn: 'textCaseSensitive',
        cell: ({ row }) => {
            const currentRow = row.original.userDetails;
            const avatar = {
                id: null,
                userName: currentRow.username,
                firstName: currentRow.firstName ?? '',
                lastName: currentRow.lastName ?? '',
                imageName: currentRow.image,
            };

            return <AvatarCell avatar={avatar} />;
        },
    },
    {
        header: 'Project Access',
        accessorKey: 'projectAccess',
        ...columnsCommonConfig.reportSettingsProjectAccess,
        cell: ({ row }) => {
            const projects = row.original.userDetails.projectsList.map((item) => item.project_name);
            if (!projects || projects.length === 0) {
                return <Chip type={'neutral'} label={'No project access'} size={'small'} />;
            }
            return (
                <DynamicChipsCell
                    list={projects}
                    type="project"
                    size="small"
                    icon={<MuiIcon icon={'folder'} type="outlined" color="var(--text-primary)" />}
                />
            );
        },
    },
    {
        header: 'Access',
        ...columnsCommonConfig.reportSettingsAccess,
        accessorKey: 'hasAccess',
        cell: ({ row }) => {
            const {
                canHaveAccess,
                hasAccess,
                userDetails: { firstName, username },
            } = row.original;
            return (
                <AllowAccessCell
                    canHaveAccess={canHaveAccess}
                    hasAccess={hasAccess}
                    firstName={firstName}
                    username={username}
                />
            );
        },
        sortingFn: customSortByAccess,
    },

    {
        header: 'Notify on upload?',
        accessorKey: 'notifyOnDataUpload',
        ...columnsCommonConfig.reportSettingsNotifyOnDataUpload,
        cell: ({ row }) => {
            const {
                canHaveAccess,
                hasAccess,
                notifyOnUpload,
                userDetails: { username },
            } = row.original;
            return (
                <NotifyOnUploadCell
                    canHaveAccess={canHaveAccess}
                    notifyOnUpload={notifyOnUpload}
                    hasAccess={hasAccess}
                    username={username}
                />
            );
        },
    },
];

export const initialState: ITableColumnState = {
    hiddenColumns: [],
    sortBy: [
        {
            id: 'reportSettingsAccess',
            desc: true,
        },
        {
            id: 'reportSettingsUserName',
            desc: false,
        },
    ],
};
